import React, { useState, useEffect, useRef } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { MessageSquare, Send, Trash2, Loader2, AlertCircle, Users } from 'lucide-react';
import { Button } from './ui/Button';
import { supabase } from '../lib/supabase';
import { useAuthStore } from '../store/authStore';
import { useTranslation } from '../lib/i18n';

interface Comment {
  id: string;
  content: string;
  user_id: string;
  created_at: string;
}

interface ConnectedUser {
  id: string;
  email: string;
  full_name: string | null;
  last_active: string;
}

interface CommentsProps {
  documentId: string;
}

const Comments: React.FC<CommentsProps> = ({ documentId }) => {
  const { user } = useAuthStore();
  const { t } = useTranslation();
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const commentsEndRef = useRef<HTMLDivElement>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [initialLoad, setInitialLoad] = useState(true);
  const [userDetails, setUserDetails] = useState<Record<string, { email: string; full_name: string | null }>>({});
  const [connectedUsers, setConnectedUsers] = useState<ConnectedUser[]>([]);

  useEffect(() => {
    loadComments();
    loadConnectedUsers();
    const subscription = subscribeToComments();
    const presenceSubscription = subscribeToPresence();
    
    // Update presence periodically
    const updatePresence = async () => {
      if (!user?.id) return;

      try {
        console.log('[PRESENCE] Updating presence for user:', user.id, 'document:', documentId);

        // First ensure user exists in users table
        const { error: userError } = await supabase
          .from('users')
          .upsert({
            id: user.id,
            email: user.email,
            full_name: user.user_metadata?.full_name
          }, {
            onConflict: 'id',
            ignoreDuplicates: false
          });

        if (userError && !userError.message.includes('duplicate')) {
          console.error('[PRESENCE] Error upserting user:', userError);
        }

        // Update presence using upsert with composite primary key
        const { data: presenceData, error: presenceError } = await supabase
          .from('user_presence')
          .upsert({
            user_id: user.id,
            document_id: documentId,
            last_active: new Date().toISOString()
          }, {
            onConflict: 'user_id,document_id',
            ignoreDuplicates: false
          })
          .select();

        if (presenceError) {
          console.error('[PRESENCE] Error upserting presence:', presenceError);
        } else {
          console.log('[PRESENCE] Presence updated successfully:', presenceData);
        }
      } catch (error) {
        console.error('[PRESENCE] Error updating presence:', error);
      }
    };

    // Initial presence update
    updatePresence();

    // Update presence every 30 seconds
    const presenceInterval = setInterval(updatePresence, 30000);

    // Cleanup function
    return () => {
      subscription.unsubscribe();
      presenceSubscription.unsubscribe();
      clearInterval(presenceInterval);

      // Remove presence when leaving
      if (user?.id) {
        supabase
          .from('user_presence')
          .delete()
          .match({ user_id: user.id, document_id: documentId })
          .then(() => {
            console.log('Presence cleaned up');
          })
          .catch(error => {
            console.error('Error cleaning up presence:', error);
          });
      }
    };
  }, [documentId, user?.id]);

  const subscribeToPresence = () => {
    return supabase
      .channel(`presence:${documentId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_presence',
          filter: `document_id=eq.${documentId}`
        },
        () => {
          loadConnectedUsers();
        }
      )
      .subscribe();
  };

  const subscribeToComments = () => {
    return supabase
      .channel(`comments:${documentId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'document_comments',
          filter: `document_id=eq.${documentId}`
        },
        async (payload) => {
          if (payload.eventType === 'INSERT') {
            const newComment = payload.new as Comment;
            setComments(prev => {
              // Check if comment already exists to prevent duplicates
              if (!prev.some(comment => comment.id === newComment.id)) {
                return [...prev, newComment].sort((a, b) => 
                  new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
                );
              }
              return prev;
            });
            await loadUserDetails([newComment.user_id]);
            scrollToBottom();
          } else if (payload.eventType === 'DELETE') {
            setComments(prev => prev.filter(comment => comment.id !== payload.old.id));
          }
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          console.log('Successfully subscribed to comments');
        }
      });
  };

  const loadConnectedUsers = async () => {
    try {
      const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000).toISOString();

      console.log('[PRESENCE] Loading connected users for document:', documentId);
      console.log('[PRESENCE] Five minutes ago timestamp:', fiveMinutesAgo);

      // Use the view that already joins presence with user data
      const { data: presenceData, error: presenceError } = await supabase
        .from('user_presence_with_users')
        .select('*')
        .eq('document_id', documentId)
        .gte('last_active', fiveMinutesAgo);

      console.log('[PRESENCE] Query result:', {
        dataCount: presenceData?.length,
        error: presenceError,
        data: presenceData
      });

      if (presenceError) {
        console.error('[PRESENCE] Error loading presence from view:', presenceError);
        throw presenceError;
      }

      if (!presenceData?.length) {
        console.log('[PRESENCE] No users found, setting empty array');
        setConnectedUsers([]);
        return;
      }

      const activeUsers = presenceData.map(presence => ({
        id: presence.user_id,
        email: presence.email || 'Unknown',
        full_name: presence.full_name,
        last_active: presence.last_active
      }));

      console.log('[PRESENCE] Setting connected users:', activeUsers);
      setConnectedUsers(activeUsers);
    } catch (error) {
      console.error('[PRESENCE] Error loading connected users:', error);
    }
  };

  const loadUserDetails = async (userIds: string[]) => {
    const uniqueUserIds = [...new Set(userIds)].filter(id => !userDetails[id]);
    if (uniqueUserIds.length === 0) return;

    try {
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('id, email, full_name')
        .in('id', uniqueUserIds);

      if (userError) throw userError;

      const newUserDetails = { ...userDetails };
      userData?.forEach(user => {
        newUserDetails[user.id] = { email: user.email, full_name: user.full_name };
      });
      setUserDetails(newUserDetails);
    } catch (error) {
      console.error('Error loading user details:', error);
    }
  };

  const loadComments = async () => {
    try {
      setError(null);

      const { data: commentsData, error: commentsError } = await supabase
        .from('document_comments')
        .select('id, content, user_id, created_at')
        .eq('document_id', documentId)
        .order('created_at', { ascending: true });

      if (commentsError) throw commentsError;

      setComments(commentsData || []);
      
      if (commentsData) {
        const userIds = commentsData.map(comment => comment.user_id);
        await loadUserDetails(userIds);
      }
    } catch (error: any) {
      console.error('Error loading comments:', error);
      setError(t('comments.failedLoad'));
    } finally {
      setLoading(false);
      setInitialLoad(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !newComment.trim()) return;

    setSubmitting(true);
    setError(null);

    try {
      const { data, error: insertError } = await supabase
        .from('document_comments')
        .insert([
          {
            document_id: documentId,
            user_id: user.id,
            content: newComment.trim()
          }
        ])
        .select()
        .single();

      if (insertError) throw insertError;

      // Clear input only after successful submission
      setNewComment('');
      
      // The new comment will be added through the subscription
    } catch (error: any) {
      console.error('Error adding comment:', error);
      setError(t('comments.failedAdd'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (commentId: string) => {
    if (!user) return;

    const confirmed = window.confirm(t('comments.confirmDelete'));
    if (!confirmed) return;

    setDeleting(commentId);
    setError(null);

    try {
      const { error: deleteError } = await supabase
        .from('document_comments')
        .delete()
        .eq('id', commentId)
        .eq('user_id', user.id);

      if (deleteError) throw deleteError;
      
      // The comment will be removed through the subscription
    } catch (error: any) {
      console.error('Error deleting comment:', error);
      setError(t('comments.failedDelete'));
      setDeleting(null);
    }
  };

  const scrollToBottom = () => {
    if (commentsEndRef.current) {
      commentsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  if (initialLoad) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow animate-pulse">
        <div className="p-4 border-b dark:border-gray-700">
          <div className="h-6 w-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
        </div>
        <div className="h-[400px] p-4">
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex space-x-3">
                <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded"></div>
                  <div className="h-4 w-full bg-gray-200 dark:bg-gray-700 rounded"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex gap-4">
      <div className="flex-1 bg-white dark:bg-gray-800 rounded-lg shadow">
        <div className="p-4 border-b dark:border-gray-700">
          <div className="flex items-center space-x-2">
            <MessageSquare className="w-5 h-5" />
            <h3 className="text-lg font-semibold">{t('comments.title')}</h3>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              ({comments.length})
            </span>
          </div>
        </div>

        {error && (
          <div className="p-4 bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-200 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <p className="text-sm">{error}</p>
          </div>
        )}

        <div className="h-[400px] overflow-y-auto p-4 space-y-4">
          {comments.length > 0 ? (
            comments.map((comment) => {
              const userDetail = userDetails[comment.user_id];
              const displayName = userDetail?.full_name || userDetail?.email || t('comments.unknownUser');
              
              return (
                <div
                  key={comment.id}
                  className="flex space-x-3 animate-fadeIn"
                >
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                      <span className="text-blue-600 dark:text-blue-400 font-medium">
                        {displayName[0].toUpperCase()}
                      </span>
                    </div>
                  </div>
                  <div className="flex-1 bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium">{displayName}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
                        </p>
                      </div>
                      {user?.id === comment.user_id && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(comment.id)}
                          disabled={deleting === comment.id}
                          className="text-red-500 hover:text-red-700"
                        >
                          {deleting === comment.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </Button>
                      )}
                    </div>
                    <p className="mt-2 text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                      {comment.content}
                    </p>
                  </div>
                </div>
              );
            })
          ) : (
            <p className="text-center text-gray-500 dark:text-gray-400 py-4">
              {t('comments.noComments')}
            </p>
          )}
          <div ref={commentsEndRef} />
        </div>

        <form onSubmit={handleSubmit} className="p-4 border-t dark:border-gray-700">
          <div className="flex space-x-2">
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder={t('comments.addPlaceholder')}
              className="flex-1 min-h-[80px] p-2 border rounded-lg resize-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600"
              disabled={submitting}
            />
            <Button
              type="submit"
              disabled={!newComment.trim() || submitting}
              className="self-end"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  {t('comments.send')}
                </>
              )}
            </Button>
          </div>
        </form>
      </div>

      {/* Connected Users Panel */}
      <div className="w-64 bg-white dark:bg-gray-800 rounded-lg shadow p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Users className="w-5 h-5 text-blue-500" />
            <h3 className="text-lg font-semibold">{t('comments.connectedUsers')}</h3>
          </div>
          <span className="text-sm text-gray-500 dark:text-gray-400 font-medium">
            ({connectedUsers.length})
          </span>
        </div>
        <div className="space-y-3">
          {connectedUsers.length > 0 ? (
            connectedUsers.map((connectedUser) => (
              <div
                key={connectedUser.id}
                className="flex items-center space-x-3 p-2 rounded-lg bg-gray-50 dark:bg-gray-700"
              >
                <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                  <span className="text-blue-600 dark:text-blue-400 font-medium">
                    {(connectedUser.full_name || connectedUser.email)[0].toUpperCase()}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">
                    {connectedUser.full_name || connectedUser.email}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {formatDistanceToNow(new Date(connectedUser.last_active), { addSuffix: true })}
                  </p>
                </div>
              </div>
            ))
          ) : (
            <p className="text-center text-gray-500 dark:text-gray-400 py-2">
              {t('comments.noUsersConnected')}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Comments;